import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getTeams, getHeadToHead } from "../lib/api";

const METRICS = [
  { key: "wins", label: "Wins", higher: true },
  { key: "winPct", label: "Win %", higher: true, fmt: (v) => v.toFixed(3) },
  { key: "pointsFor", label: "Points For", higher: true },
  { key: "pointsAgainst", label: "Points Against", higher: false },
  { key: "diff", label: "Point Diff", higher: true, derive: (t) => t.pointsFor - t.pointsAgainst, fmt: (v) => (v > 0 ? `+${v}` : v) },
];

const CompareRow = ({ metric, a, b }) => {
  const va = metric.derive ? metric.derive(a) : a[metric.key];
  const vb = metric.derive ? metric.derive(b) : b[metric.key];
  const aWins = metric.higher ? va > vb : va < vb;
  const bWins = metric.higher ? vb > va : vb < va;
  const total = Math.abs(va) + Math.abs(vb) || 1;
  const pa = (Math.abs(va) / total) * 100;
  const fmt = metric.fmt || ((v) => v);
  return (
    <div data-testid={`compare-${metric.key}`} className="py-3 border-b border-border/50 last:border-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`font-display text-2xl tabular-nums ${aWins ? "text-primary" : "text-muted-foreground"}`}>{fmt(va)}</span>
        <span className="label-caps">{metric.label}</span>
        <span className={`font-display text-2xl tabular-nums ${bWins ? "text-accent" : "text-muted-foreground"}`}>{fmt(vb)}</span>
      </div>
      <div className="flex h-2 bg-secondary overflow-hidden">
        <div className="bg-primary transition-all duration-500" style={{ width: `${pa}%` }} />
        <div className="bg-accent transition-all duration-500" style={{ width: `${100 - pa}%` }} />
      </div>
    </div>
  );
};

const TeamPicker = ({ teams, value, onChange, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-card border border-border px-3 h-11 font-semibold outline-none focus:border-primary transition-colors duration-150"
  >
    {teams.map((t) => (
      <option key={t.id} value={t.id}>{t.fullName}</option>
    ))}
  </select>
);

export default function Compare() {
  const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams });
  const [a, setA] = useState("");
  const [b, setB] = useState("");

  useEffect(() => {
    if (teams.length && !a && !b) {
      setA(teams[0].id);
      setB(teams[1]?.id || teams[0].id);
    }
  }, [teams, a, b]);

  const { data } = useQuery({
    queryKey: ["h2h", a, b],
    queryFn: () => getHeadToHead(a, b),
    enabled: !!a && !!b && a !== b,
  });

  const ta = data?.teamA;
  const tb = data?.teamB;

  return (
    <div data-testid="compare-page" className="space-y-8">
      <div className="flex items-end justify-between border-b-2 border-border pb-2">
        <h1 className="font-display text-5xl">HEAD TO HEAD</h1>
        <span className="label-caps">Team Comparison</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <TeamPicker teams={teams} value={a} onChange={setA} testid="compare-select-a" />
        <TeamPicker teams={teams} value={b} onChange={setB} testid="compare-select-b" />
      </div>

      {a === b && <p className="text-muted-foreground">Pick two different teams to compare.</p>}

      {ta && tb && a !== b && (
        <>
          <div className="grid grid-cols-2 gap-4">
            {[ta, tb].map((t, i) => (
              <Link
                key={t.id}
                to={`/team/${t.id}`}
                className="relative border border-border overflow-hidden p-5 flex items-center gap-4 hover:border-primary transition-colors duration-150"
                style={{ background: `linear-gradient(${i ? "270deg" : "90deg"}, ${t.color}, transparent)` }}
              >
                <img src={t.logo} alt={t.abbr} className="w-14 h-14 object-contain" />
                <div>
                  <p className="font-display text-2xl leading-none">{t.fullName}</p>
                  <p className="text-sm text-muted-foreground">{t.wins}-{t.losses} · {t.division}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="border border-border bg-card px-5 py-2">
            {METRICS.map((m) => (
              <CompareRow key={m.key} metric={m} a={ta} b={tb} />
            ))}
          </div>

          <section>
            <h2 className="font-display text-3xl mb-4 border-b-2 border-border pb-2">
              MATCHUPS {data.matchups.length ? "" : "· NONE THIS SEASON"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.matchups.map((g) => {
                const homeWin = g.status === "final" && g.homeScore > g.awayScore;
                return (
                  <div key={g.id} data-testid={`h2h-game-${g.id}`} className="border border-border bg-card p-4">
                    <p className="label-caps mb-2">{g.week ? `Week ${g.week}` : g.round} · {g.stadium}</p>
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <img src={g.awayLogo} alt={g.awayTeam} className="w-7 h-7 object-contain" />
                        <span className="font-semibold">{g.awayTeam}</span>
                      </span>
                      <span className="font-display text-3xl tabular-nums">
                        <span className={!homeWin && g.status === "final" ? "text-primary" : "text-muted-foreground"}>{g.awayScore ?? "-"}</span>
                        <span className="text-muted-foreground mx-2">:</span>
                        <span className={homeWin ? "text-primary" : "text-muted-foreground"}>{g.homeScore ?? "-"}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-semibold">{g.homeTeam}</span>
                        <img src={g.homeLogo} alt={g.homeTeam} className="w-7 h-7 object-contain" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
